import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom, from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { MeddelandeInfotavlaDTO } from "~app/common/models/meddelandeInfotavla/MeddelandeInfotavlaDTO";
import { IWebInstallningMeddelandeInfotavla } from "~app/common/models/webInstallning-models";
import { ApiClientService } from "~app/common/services/api-client.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";

@Injectable({
    providedIn: "root"
})
export class SystemaktivitetService {
    allMeddelandeInfotavlor$: BehaviorSubject<MeddelandeInfotavlaDTO[]>;
    currentMeddelandeInfotavlor$: BehaviorSubject<MeddelandeInfotavlaDTO[]>;
    dismissedMeddelandeInfotavlorIds: IWebInstallningMeddelandeInfotavla;

    private readonly apiClient = inject(ApiClientService);
    private readonly webSettingsKey = "MeddelandeInfotavla";
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.currentMeddelandeInfotavlor$ = new BehaviorSubject<MeddelandeInfotavlaDTO[]>([]);
        this.allMeddelandeInfotavlor$ = new BehaviorSubject<MeddelandeInfotavlaDTO[]>([]);

        this.webinstallningarService.webinstallningar$.subscribe(x => {
            this.refreshActiveMeddelandeTavlor();
        });
    }

    createSystemaktivitet(nyttMeddelande: MeddelandeInfotavlaDTO): Observable<void> {
        return from(firstValueFrom(this.apiClient.post<MeddelandeInfotavlaDTO, MeddelandeInfotavlaDTO>("meddelandeinfotavla", nyttMeddelande)).then(x => {
            this.refreshMeddelandeInfotavlor();
            this.refreshActiveMeddelandeTavlor();
        }
        ));
    }

    deleteSystemaktivitet(systemaktivitet: MeddelandeInfotavlaDTO): Observable<void> {
        return from(firstValueFrom(this.apiClient.delete(`meddelandeinfotavla/${systemaktivitet.id}`)).then(x => {
            this.refreshMeddelandeInfotavlor();
            this.refreshActiveMeddelandeTavlor();
        }
        ));
    }

    dismissMeddelandeInfotavla(systemaktivitet: MeddelandeInfotavlaDTO): void {
        this.dismissedMeddelandeInfotavlorIds = this.webinstallningarService.get<IWebInstallningMeddelandeInfotavla>(this.webSettingsKey);

        if (!this.dismissedMeddelandeInfotavlorIds) {
            this.dismissedMeddelandeInfotavlorIds = {
                dismissedIds: []
            };
        }

        if (this.dismissedMeddelandeInfotavlorIds.dismissedIds.indexOf(systemaktivitet.id) === -1) {
            this.dismissedMeddelandeInfotavlorIds.dismissedIds.push(systemaktivitet.id);
        }

        this.webinstallningarService.set<IWebInstallningMeddelandeInfotavla>(this.webSettingsKey, this.dismissedMeddelandeInfotavlorIds);

        this.refreshActiveMeddelandeTavlor();
    }

    async refreshActiveMeddelandeTavlor() {
        this.dismissedMeddelandeInfotavlorIds = await this.webinstallningarService.getAsync<IWebInstallningMeddelandeInfotavla>(this.webSettingsKey);
        this.apiClient.get<MeddelandeInfotavlaDTO[]>("meddelandeinfotavla/active")
            .pipe(
                map((meddelande: MeddelandeInfotavlaDTO[]) => this.filterMeddelandeInfoTavlor(meddelande)
                ))
            .subscribe(x => {
                this.currentMeddelandeInfotavlor$.next(x);
            });
    }

    refreshMeddelandeInfotavlor(): void {
        this.apiClient.get<MeddelandeInfotavlaDTO[]>("meddelandeinfotavla")
            .subscribe(x => {
                this.allMeddelandeInfotavlor$.next(x);
            });
    }

    updateSystemaktivitet(meddelande: MeddelandeInfotavlaDTO): Observable<void> {
        return from(firstValueFrom(this.apiClient.put<MeddelandeInfotavlaDTO, MeddelandeInfotavlaDTO>(`meddelandeinfotavla/${meddelande.id}`, meddelande)).then(x => {
            this.refreshMeddelandeInfotavlor();
            this.refreshActiveMeddelandeTavlor();
        }
        ));
    }

    private filterMeddelandeInfoTavlor(source: MeddelandeInfotavlaDTO[]): MeddelandeInfotavlaDTO[] {
        if (!this.dismissedMeddelandeInfotavlorIds || !this.dismissedMeddelandeInfotavlorIds.dismissedIds) {
            return source;
        }

        return source.filter(meddelandeInfotavla => this.dismissedMeddelandeInfotavlorIds.dismissedIds.indexOf(meddelandeInfotavla.id) === -1);
    }
}
